import { useIsTouch } from '../hooks/useMediaQuery';
import type { ShadeMode } from '../lib/types';

interface Props {
  shadeMode: ShadeMode;
  showBuildings: boolean;
  canReset: boolean;
  onShadeMode(mode: ShadeMode): void;
  onShowBuildings(show: boolean): void;
  onReset(): void;
  /** See StatsPanel: `flow` sheds the floating-panel chrome for the sheet. */
  variant?: 'panel' | 'flow';
}

/**
 * The canopy and its ground shadow were separate toggles, which invited the
 * incoherent combinations (analyzed canopy over a neutral shadow, and worse)
 * and made "Analysis" appear twice meaning two different things. They are one
 * decision -- show the shade analysis, or set it aside to look at the canopy --
 * so they are one control.
 */
const SHADE_CAPTION: Record<ShadeMode, string> = {
  analysis: 'Canopy and shadow colored by what they shade',
  foliage: 'Plain green canopy, neutral shadow — for looking at shape',
};

/**
 * One switch per layer.
 *
 * The Shown/Hidden pair spent two segments and four words on a binary that is
 * on or off, and read as a choice between two modes rather than as a layer you
 * turn on. A switch says the state and the affordance in one object, and both
 * controls now sit off at rest -- so the map opens on the canopy itself and
 * the analysis is something the visitor adds.
 */
function Toggle({
  label,
  on,
  title,
  onChange,
}: {
  label: string;
  on: boolean;
  title: string;
  onChange(next: boolean): void;
}) {
  return (
    <button
      type="button"
      className="toggle"
      role="switch"
      aria-checked={on}
      title={title}
      onClick={() => onChange(!on)}
    >
      <span className="toggle__label">{label}</span>
      <span className="toggle__track" aria-hidden="true">
        <span className="toggle__knob" />
      </span>
    </button>
  );
}

export function AppearanceControls({
  shadeMode,
  showBuildings,
  canReset,
  onShadeMode,
  onShowBuildings,
  onReset,
  variant = 'panel',
}: Props) {
  // See StatsPanel: the gesture is named for the device in hand.
  const verb = useIsTouch() ? 'tap' : 'click';

  return (
    <aside
      className={variant === 'panel' ? 'controls panel enter enter--1' : 'controls controls--flow'}
      aria-label="Appearance"
    >
      <div className="control-group">
        <Toggle
          label="Shade analysis"
          on={shadeMode === 'analysis'}
          title="Color canopy and ground shadow by whether each point shades open ground, shades a building, or is itself in shade"
          onChange={(on) => onShadeMode(on ? 'analysis' : 'foliage')}
        />
        <p className="control-caption">{SHADE_CAPTION[shadeMode]}</p>
      </div>

      <div className="control-group">
        <Toggle
          label="Buildings"
          on={showBuildings}
          title="Extrude building footprints to their roof height. Buildings cast shadows and classify canopy either way."
          onChange={onShowBuildings}
        />
        <p className="control-caption">
          {showBuildings
            ? `Massing shown; ${verb} one to measure against it alone`
            : `Massing hidden; footprints still respond to a ${verb}`}
        </p>
      </div>

      <button type="button" className="btn-reset" onClick={onReset} disabled={!canReset}>
        Clear selection
      </button>
    </aside>
  );
}
